import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { dashboardApi } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import type { MachineDashboardRow } from "../../types/machinesDashboard";
import "../../styles/irinox.css";

type ChartDataPoint = {
  time: string;
  consumption: number;
  expense: number;
};

export function IrinoxConsumptionChart({
  machines,
}: {
  machines: MachineDashboardRow[];
}) {
  const { token } = useAuth() as { token: string | null };
  const [selectedId, setSelectedId] = useState<string>("");
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const machineId = selectedId || machines[0]?.id;

  useEffect(() => {
    if (!machineId || !token) return;

    setLoading(true);
    setErr(null);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const startTimestamp = todayStart.getTime();

    dashboardApi
      .getIrinoxEnergyChart(token, machineId, startTimestamp)
      .then((res) => {
        const series = Array.isArray(res.series) ? res.series : [];
        const findSeries = (matcher: (label: string) => boolean) =>
          series.find(
            (s) => typeof s.label === "string" && matcher(s.label.toLowerCase())
          );

        const consumption =
          findSeries((lbl) =>
            lbl.includes("estimatedconsumption") ||
            lbl.includes("consumption") ||
            lbl.includes("consumo")
          ) ?? null;

        const expense =
          findSeries((lbl) =>
            lbl.includes("estimatedexpense") ||
            lbl.includes("expense") ||
            lbl.includes("spesa")
          ) ?? null;

        const labels = res.labels ?? [];
        const len = Math.max(
          consumption?.data?.length ?? 0,
          expense?.data?.length ?? 0,
          labels.length
        );

        const points: ChartDataPoint[] = [];
        for (let i = 0; i < len; i++) {
          points.push({
            time: labels[i] ?? `${i}:00`,
            consumption: Number(consumption?.data?.[i] ?? 0),
            expense: Number(expense?.data?.[i] ?? 0),
          });
        }
        if (points.length > 0 && points.some((p) => p.consumption > 0 || p.expense > 0)) {
          setData(points);
          return;
        }
        // Fallback: se chart vuoto, prova appliances e mostra consumo totale per questa macchina
        const sel = machines.find((m) => m.id === machineId);
        const extId = (sel?.externalId ?? "").toString().toUpperCase();
        if (extId) {
          dashboardApi
            .getIrinoxEnergyAppliances(token, startTimestamp)
            .then((apps) => {
              const app = apps.find(
                (a) => (a.serialNumber ?? "").toString().toUpperCase() === extId
              );
              if (app && ((app.consumption ?? 0) > 0 || (app.expense ?? 0) > 0)) {
                setData([
                  {
                    time: "Oggi",
                    consumption: app.consumption ?? 0,
                    expense: app.expense ?? 0,
                  },
                ]);
              } else {
                setData([]);
              }
            })
            .catch(() => setData([]));
        } else {
          setData([]);
        }
      })
      .catch((e) => {
        setErr(e?.message ?? "Errore caricamento consumi");
        setData([]);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- machineId derives from machines; full machines in deps causes extra runs
  }, [machineId, token, machines]);

  if (!machines.length) return null;

  const selectedMachine = machines.find((m) => m.id === machineId) ?? machines[0];

  return (
    <div className="irinox-card mb-4">
      <div className="irinox-card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="d-flex align-items-center gap-2">
          <i className="bi bi-graph-up" />
          <span>Consumi giornalieri Irinox</span>
        </span>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {selectedMachine && (
            <span className="badge bg-light text-dark small">
              {selectedMachine.name ?? selectedMachine.externalId ?? selectedMachine.id}
            </span>
          )}
          <select
            className="form-select form-select-sm"
            style={{ minWidth: 220 }}
            value={machineId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ?? m.externalId ?? m.id}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="card-body irinox-chart-container" style={{ height: 320 }}>
        {loading && (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted irinox-loading">
            <span className="spinner-border spinner-border-sm me-2" />
            Caricamento consumi…
          </div>
        )}
        {err && !loading && (
          <div className="d-flex align-items-center justify-content-center h-100 text-danger small">
            {err}
          </div>
        )}
        {!loading && !err && data.length > 0 && (
          <ResponsiveContainer width="100%" height={280} minHeight={200}>
            <ComposedChart data={data} margin={{ top: 10, right: 50, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" label={{ value: "kWh", angle: -90, position: "insideLeft" }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: "€", angle: 90, position: "insideRight" }} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  name === "consumption"
                    ? `${Number(value).toFixed(2)} kWh`
                    : `€ ${Number(value).toFixed(2)}`,
                  name === "consumption" ? "Consumo" : "Spesa stimata",
                ]}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="consumption"
                fill="#0d6efd"
                name="Consumo (kWh)"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="expense"
                stroke="#198754"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Spesa stimata (€)"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {!loading && !err && data.length === 0 && (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
            Nessun dato disponibile per oggi
          </div>
        )}
      </div>
    </div>
  );
}

